/**
 * Module dependencies.
 */

import { Me } from './use-me';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Export `AdminUser` type — the current-user shape plus admin-list extras.
 */

export type AdminUser = Me & {
  lastAccessAt?: string | null;
  bankAccountsCount?: number;
};

/**
 * Editable user payload.
 */

export type AdminUserPayload = Partial<Pick<AdminUser, 'firstName' | 'lastName' | 'profilePhotoUrl' | 'isAdmin'>>;

/**
 * Export `useUsers` — list every user (admin only).
 */

export function useUsers() {
  const client = useRequestProvider();

  return useQuery<AdminUser[]>(['adminUsers'], {
    queryFn: async () => client.get(resolveAppUrl('adminUsers')).then(({ data }: { data: any }) => data?.data ?? [])
  });
}

/**
 * Export `useUser` — a single user (admin only).
 */

export function useUser(id: string | number | undefined) {
  const client = useRequestProvider();

  return useQuery<AdminUser>(['adminUser', id], {
    enabled: id !== undefined,
    queryFn: async () =>
      client
        .get(resolveAppUrl('adminUserById', { interpolations: { id } }))
        .then(({ data }: { data: any }) => data?.data)
  });
}

/**
 * Export `useUpdateUser` — patch a user's fields (admin only).
 */

export function useUpdateUser(id: string | number | undefined) {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AdminUserPayload): Promise<AdminUser> =>
      client
        .patch(resolveAppUrl('adminUserById', { interpolations: { id } }), payload)
        .then(({ data }: { data: any }) => data?.data),
    onSuccess: user => {
      queryClient.setQueryData(['adminUser', id], user);
      queryClient.invalidateQueries(['adminUsers']);
    }
  });
}

/**
 * Export `useUpdateUserPassword` — set a user's password (admin only).
 */

export function useUpdateUserPassword(id: string | number | undefined) {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (password: string) =>
      client
        .post(resolveAppUrl('adminUserPassword', { interpolations: { id } }), { password })
        .then(({ data }: { data: any }) => data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminUser', id]);
    }
  });
}

/**
 * Export `useImpersonateUser` — start impersonating a user (admin only). The
 * caller should hard-reload afterwards so the whole app picks up the new session.
 */

export function useImpersonateUser() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async (id: string | number) =>
      client
        .post(resolveAppUrl('adminUserImpersonate', { interpolations: { id } }))
        .then(({ data }: { data: any }) => data)
  });
}

/**
 * Export `useStopImpersonating` — return to the acting admin's session.
 */

export function useStopImpersonating() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async () => client.post(resolveAppUrl('stopImpersonating')).then(({ data }: { data: any }) => data)
  });
}

/**
 * Export `useDeleteUser` — remove a user (admin only).
 */

export function useDeleteUser() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) =>
      client.delete(resolveAppUrl('adminUserById', { interpolations: { id } })).then(({ data }: { data: any }) => data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminUsers']);
    }
  });
}
